import React from 'react';
import { useRemoteAttendanceList } from './hooks/useRemoteAttendanceList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import RemoteAttendanceTable from './components/RemoteAttendanceTable';

export default function RemoteAttendanceListScreen() {
  const { state, actions } = useRemoteAttendanceList();

  const statusTabs = [
    { key: 'Pending', label: 'Pending' },
    { key: 'Approved', label: 'Approved' },
    { key: 'Rejected', label: 'Rejected' },
    { key: 'All', label: 'All' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner with Title on Left, Status Toggle on Right */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Remote Attendance
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Status Toggle Filter on the Right */}
          <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
            {statusTabs.map((tab) => {
              const isActive = state.statusFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => actions.handleStatusTabChange(tab.key)}
                  className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                    isActive
                      ? 'bg-primary text-on-primary shadow-sm'
                      : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shared Filters Component with Upload & Reporting tools */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        statusFilter={state.statusFilter}
        showStatus={false}
        employeeFilter={state.employeeFilter}
        reasonFilter={state.reasonFilter}
        datePreset={state.datePresetFilter}
        datePresetFilter={state.datePresetFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setEmployeeFilter={actions.setEmployeeFilter}
        setReasonFilter={actions.setReasonFilter}
        setDatePreset={actions.setDatePreset}
        setDatePresetFilter={actions.setDatePresetFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}

        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}

        searchFieldOptions={state.searchFieldOptions}
        reasonOptions={state.reasonOptions}
        datePresetOptions={state.datePresetOptions}

        visibleAccordionFilters={[
          'employee',
          'reason',
          'datePreset',
          'fromDate',
          'toDate',
        ]}

        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Remote Attendance Table */}
      <section className="space-y-4">
        <RemoteAttendanceTable
          records={state.records}
          isLoading={state.isLoading}
          onApprove={actions.handleApprove}
          onReject={actions.handleReject}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}

